import axios, { AxiosResponse } from 'axios';
import queryString from 'query-string';
import 'url-search-params-polyfill';
import { apiUrl } from '@/env';
import { getLocalToken } from '@/utils';
import {
  IUserProfile,
  IUserProfileUpdate,
  IUserProfileCreate,
  TableData,
  IDRec,
  ChoiceOptionList
} from '@/interfaces';
import {
  commitAddNotification,
  commitRemovePersistentNotifications
} from './store/main/mutations';

function authHeaders(token: string) {
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
}

function handleError(store, error, message = 'Er is een fout opgetreden') {
  if (error.response.status === 403) {
    // Token expired
    sessionStorage.setItem(
      'redirectPath',
      window.location.pathname + window.location.search
    );
    window.location.href = '/login';
  } else {
    if (store) {
      // Conflict
      if (error.response.status === 409) {
        switch (error.response.data.detail) {
          case 'IntegrityError':
            commitAddNotification(store, {
              content:
                'De gegevens zijn nog in gebruik en kunnen niet verwijderd worden',
              color: 'warning'
            });
            break;
          case 'SerialNumberNotUnique':
            commitAddNotification(store, {
              content:
                'Dit serienummer is al in gebruik en kan niet gebruikt worden',
              color: 'warning'
            });
            break;
          case 'ReportCodeNotUnique':
            commitAddNotification(store, {
              content:
                'Dit dossier nummer is al in gebruik en kan niet gebruikt worden',
              color: 'warning'
            });
            break;
          default:
            commitAddNotification(store, {
              content: message,
              color: 'error'
            });
        }
      } else {
        commitAddNotification(store, {
          content: message,
          color: 'error'
        });
      }
    }
  }
  return null;
}

function getApiUrl(relUrl: string) {
  return `${apiUrl}/api/v1${relUrl}`;
}

function parseFileNameFromURL(urlStr: string): string | undefined {
  const url = new URL(urlStr);
  return url.pathname.split('/').pop();
}

function downloadFromResponse(response: AxiosResponse) {
  let fileName = '';
  // This header is not exposed for CORS requests -> fileName will be empty when testing locally
  const disposition = response.headers['content-disposition'];
  if (disposition && disposition.indexOf('attachment') !== -1) {
    // Get filename with content-disposition header
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
  } else {
    const lastPart = parseFileNameFromURL(response.request.responseURL);
    if (lastPart) {
      fileName = lastPart;
    }
  }
  const blob = new Blob([response.data], {
    type: response.headers['content-type']
  });
  const fileURL = window.URL.createObjectURL(blob);
  const fileLink = document.createElement('a');
  document.body.appendChild(fileLink); // Add the element to the DOM; firefox fix
  (fileLink as any).style = 'display: none';
  fileLink.href = fileURL;
  fileLink.download = fileName;
  fileLink.click();
  window.URL.revokeObjectURL(fileLink.href);
}

export const api = {
  // parseFileNameFromURL = parseFileNameFromURL;
  async logInGetToken(username: string, password: string, otpcode: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('otpcode', otpcode);

    return axios.post(`${apiUrl}/api/v1/login/access-token`, params, {
      validateStatus: status => {
        return true;
      }
    });
  },

  async getMe(token: string) {
    return axios.get<IUserProfile>(
      `${apiUrl}/api/v1/users/me`,
      authHeaders(token)
    );
  },

  async updateMe(token: string, data: IUserProfileUpdate) {
    return axios.put<IUserProfile>(
      `${apiUrl}/api/v1/users/me`,
      data,
      authHeaders(token)
    );
  },

  async getUsers(token: string) {
    return axios.get<IUserProfile[]>(
      `${apiUrl}/api/v1/users/`,
      authHeaders(token)
    );
  },

  async updateUser(token: string, userId: number, data: IUserProfileUpdate) {
    return axios.put(
      `${apiUrl}/api/v1/users/${userId}`,
      data,
      authHeaders(token)
    );
  },

  async createUser(token: string, data: IUserProfileCreate) {
    return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token));
  },

  async passwordRecovery(email: string) {
    return axios.post(`${apiUrl}/api/v1/password-recovery/${email}`);
  },

  async resetPassword(password: string, token: string) {
    return axios.post(`${apiUrl}/api/v1/reset-password/`, {
      new_password: password,
      token
    });
  },

  async getTableData(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj !== {}) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<TableData>(url, authHeaders(token)).then(
        response => {
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async getChoices(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<ChoiceOptionList>(url, authHeaders(token)).then(
        response => {
          return response.data;
        },
        error => {
          return handleError(
            store,
            error,
            'Er is een fout opgetreden bij het ophalen van een keuzelijst'
          );
        }
      );
    }
  },

  async downloadFileWithLink(store, url, directDownload = true) {
    if (directDownload) {
      const fileLink = document.createElement('a');
      document.body.appendChild(fileLink); // Add the element to the DOM; firefox fix
      (fileLink as any).style = 'display: none';
      fileLink.href = url + '?for_download=1';
      fileLink.click();
      window.URL.revokeObjectURL(fileLink.href);
    } else {
      if (store) {
        commitAddNotification(store, {
          content: 'Downloaden...',
          showProgress: true,
          persistent: true,
          color: 'primary'
        });
      }
      axios({
        method: 'get',
        url,
        responseType: 'blob'
      }).then(
        response => {
          downloadFromResponse(response);
          commitRemovePersistentNotifications(store);
          commitAddNotification(store, {
            content: 'Download geslaagd',
            color: 'success'
          });
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error, 'Er is een fout opgetreden');
        }
      );
    }
  },

  async downloadAction(store, relUrl: string, data: object = {}) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {

      return axios.post<string>(url, data, authHeaders(token)).then(
        response => {
          api.downloadFileWithLink(store, response.data);
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error);
        }
      );
    }
  },

  async downloadFile(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl + '/url');
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
        return axios.get<string>(url, authHeaders(token)).then(
        response => {
          api.downloadFileWithLink(store, response.data);
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error);
        }
      );
    }
  },

  async openFile(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl + '/url');
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<string>(url, authHeaders(token)).then(
        response => {
          if (window) {
            const newWindow = window.open(response.data, '_blank');
            if (newWindow) {
              newWindow.focus();
            }
          }
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error);
        }
      );
    }
  },

  async getFileURL(store, relUrl: string, dataurl: boolean = true) {
    const url = getApiUrl(relUrl + (dataurl ? '/dataurl' : '/url'));
    const token = getLocalToken();
    if (token) {
      return axios.get(url, authHeaders(token)).then(
        response => {
          return response.data;
        },
        error => {
          if (error.response.status === 404) {
            return {};
          } else {
            commitRemovePersistentNotifications(store);
            return handleError(store, error);
          }
        }
      );
    }
  },

  async callValidator(store, valtype: string, v: string) {
    const url = getApiUrl('/validators/' + valtype);
    return axios.get(url, { params: { v } }).then(
      response => {
        return response.data;
      },
      error => {
        return handleError(store, error);
      }
    );
  },

  async getRecord(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<IDRec>(url, authHeaders(token)).then(
        response => {
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async createRecord(store, relUrl: string, rec: object) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<IDRec>(url, rec, authHeaders(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Aangemaakt',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async updateRecord(store, relUrl: string, rec: object) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.put<IDRec>(url, rec, authHeaders(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Opgeslagen',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async deleteRecord(store, relUrl: string) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.delete(url, authHeaders(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Verwijderd',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async duplicateRecord(
    store,
    relUrl: string,
    queryObj: object,
    data: object = {}
  ) {
    let url = getApiUrl(relUrl + '/duplicate');
    if (queryObj !== {}) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.post<IDRec>(url, data, authHeaders(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Actie geslaagd',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async postAction(store, relUrl: string, data: object = {}) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<string>(url, data, authHeaders(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Actie geslaagd',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async uploadFile(store, relUrl: string, data: object) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<IDRec>(url, data, authHeaders(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Upload geslaagd',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(
            store,
            error,
            'Er is een fout opgetreden bij het uploaden van data'
          );
        }
      );
    }
  }
};
